/**
 * Atomic booking primitives. These run inside ONE storage transaction (see StorageAdapter.transact),
 * so the availability re-check and the write cannot interleave with another tab's booking.
 */
import { canBook, type AvailabilityData, type BookFailReason } from './availability';
import { DAY, toIso, toMs } from './time';
import type { Tx } from './tx';
import {
  newId, type Appointment, type AppointmentSource, type AppointmentStatus, type DepositStatus, type Id, type IsoUtc, type OnlineMethod, type Slot,
} from './types';

export interface BookingDraft {
  businessId: Id;
  clientId: Id;
  serviceId: Id;
  staffId: Id | 'any';
  start: IsoUtc;
  source: AppointmentSource;
  status: AppointmentStatus;
  manageToken: string;
  priceAgorot: number;
  depositAgorot: number;
  depositStatus: DepositStatus;
  holdExpiresAt?: IsoUtc;
  depositMethod?: OnlineMethod;
  note?: string;
}
export type BookFailure =
  | { ok: false; reason: 'conflict'; nextSlots: Slot[] }
  | { ok: false; reason: Exclude<BookFailReason, 'conflict'> | 'client_not_found' | 'service_not_found' | 'business_not_found' };
export type BookResult = { ok: true; appointment: Appointment } | BookFailure;

export interface MoveRequest {
  appointmentId: Id;
  start: IsoUtc;
  /** undefined keeps the current staff member; 'any' lets the engine pick. */
  staffId?: Id | 'any';
  source: AppointmentSource;
}
export type MoveFailure = BookFailure | { ok: false; reason: 'appointment_not_found' | 'not_movable' };
export type MoveResult = { ok: true; appointment: Appointment } | MoveFailure;

async function loadData(tx: Tx, businessId: Id, aroundMs: number): Promise<AvailabilityData | undefined> {
  const business = await tx.get('businesses', businessId);
  const settings = await tx.get('settings', businessId);
  if (!business || !settings) return undefined;
  const where = { businessId };
  return {
    tz: business.tz,
    settings,
    staff: await tx.list('staff', { where }),
    weeklyHours: await tx.list('weeklyHours', { where }),
    timeOff: await tx.list('timeOff', { where }),
    appointments: await tx.list('appointments', { where, range: { field: 'start', gte: toIso(aroundMs - 2 * DAY) } }),
  };
}

/** Re-checks availability and inserts the appointment, all inside the caller's transaction. */
export async function bookInTx(tx: Tx, draft: BookingDraft, now: IsoUtc): Promise<BookResult> {
  const nowMs = toMs(now);
  const data = await loadData(tx, draft.businessId, toMs(draft.start));
  if (!data) return { ok: false, reason: 'business_not_found' };
  const service = await tx.get('services', draft.serviceId);
  if (!service || service.businessId !== draft.businessId) return { ok: false, reason: 'service_not_found' };
  const client = await tx.get('clients', draft.clientId);
  if (!client || client.businessId !== draft.businessId) return { ok: false, reason: 'client_not_found' };
  const check = canBook({ ...data, now: nowMs }, service, draft);
  if (!check.ok) return check.reason === 'conflict' ? { ok: false, reason: 'conflict', nextSlots: check.nextSlots } : { ok: false, reason: check.reason };
  const appointment: Appointment = {
    id: newId(nowMs), businessId: draft.businessId, createdAt: now, updatedAt: now,
    clientId: draft.clientId, serviceId: draft.serviceId, staffId: check.staffId,
    start: check.start, end: check.end, blockedUntil: check.blockedUntil,
    status: draft.status, source: draft.source, manageToken: draft.manageToken,
    priceAgorot: draft.priceAgorot, depositAgorot: draft.depositAgorot, depositStatus: draft.depositStatus,
    ...(draft.holdExpiresAt ? { holdExpiresAt: draft.holdExpiresAt } : {}),
    ...(draft.depositMethod ? { depositMethod: draft.depositMethod } : {}),
    ...(draft.note ? { note: draft.note } : {}),
  };
  await tx.put('appointments', appointment);
  return { ok: true, appointment };
}

/** Moves an appointment atomically (the appointment itself never conflicts with its old slot). */
export async function moveInTx(tx: Tx, req: MoveRequest, now: IsoUtc): Promise<MoveResult> {
  const current = await tx.get('appointments', req.appointmentId);
  if (!current) return { ok: false, reason: 'appointment_not_found' };
  if (!['pending', 'confirmed', 'awaiting_deposit'].includes(current.status)) return { ok: false, reason: 'not_movable' };
  const data = await loadData(tx, current.businessId, toMs(req.start));
  if (!data) return { ok: false, reason: 'business_not_found' };
  const service = await tx.get('services', current.serviceId);
  if (!service) return { ok: false, reason: 'service_not_found' };
  const check = canBook(
    { ...data, now: toMs(now), excludeAppointmentId: current.id },
    service,
    { serviceId: service.id, staffId: req.staffId ?? current.staffId, start: req.start, source: req.source },
  );
  if (!check.ok) return check.reason === 'conflict' ? { ok: false, reason: 'conflict', nextSlots: check.nextSlots } : { ok: false, reason: check.reason };
  const moved: Appointment = {
    ...current, staffId: check.staffId, start: check.start, end: check.end, blockedUntil: check.blockedUntil,
    previousStart: current.start, updatedAt: now,
  };
  await tx.put('appointments', moved);
  return { ok: true, appointment: moved };
}
